// Boot-time timezone capture.
//
// We need the user's IANA timezone server-side so email / WhatsApp invites
// can render times in the recipient's local zone. The browser hands it to
// us for free via `Intl.DateTimeFormat().resolvedOptions().timeZone`, so we
// just upsert it once per tab/session — debounced via sessionStorage so a
// returning user on the same device costs zero requests.
//
// `enabled` is passed in by callers (typically `!!me`) so we only fire
// after auth resolves. The mutation is fire-and-forget; analytics-grade
// telemetry, not a critical path.

import { useEffect } from 'react';
import { useGql } from '@ah/design';
import { SET_MY_TIMEZONE_M } from './queries.ts';

const SENT_KEY = 'sc.tz.sent';

function detectTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz || tz === 'Etc/Unknown') return null;
    return tz;
  } catch {
    return null;
  }
}

/**
 * Detects the browser timezone and upserts it to the server once per tab
 * session, only if it differs from what we sent last time (so we don't
 * touch the DB on every reload). Safe to call from any logged-in app shell.
 */
export function useTimezoneSync(enabled: boolean): void {
  const gql = useGql();
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    const tz = detectTimezone();
    if (!tz) return;
    let lastSent: string | null = null;
    try {
      lastSent = window.sessionStorage.getItem(SENT_KEY);
    } catch {
      // sessionStorage may be unavailable (Safari private mode, etc.) —
      // fall through and just send. Worst case we issue one extra mutation
      // per tab, which the server short-circuits if unchanged anyway.
    }
    if (lastSent === tz) return;
    gql
      .request(SET_MY_TIMEZONE_M, { timezone: tz })
      .then(() => {
        try {
          window.sessionStorage.setItem(SENT_KEY, tz);
        } catch {
          // ignore — see comment above
        }
      })
      .catch((err: unknown) => {
        if (typeof console !== 'undefined') console.debug('[timezone] sync failed', err);
      });
  }, [enabled, gql]);
}
